import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import InputMask from 'react-input-mask';

import { Error } from '../FormField/Error';
import { Icons } from '../FormField/Icons';
import { WrapperField } from '../FormField/WrapperField';
import { Input } from '../Input';

import styles from '../FormField/FormField.module.scss';

export enum MaskCarNumber {
  Russia = 'А000АА000',
}

interface InputCarNumberProps {
  id?: string;
  className?: string;
  name: string;
  register?: (Ref, RegisterOptions?) => { onChange; onBlur; name; ref };
  placeholder?: MaskCarNumber;
  value: string;
  maxLength?: number;
  dataTestId?: string;
  handleFocusAndBlur?: () => void;
}

interface InputCarNumberWrapperProps extends InputCarNumberProps {
  error?: string;
  label?: string;
  isFocused?: boolean;
  isRequired?: boolean;
  isFetching?: boolean;
  isRemovingSpaces?: boolean;
  isValid?: boolean;
  helperMessage?: string;
  setValue: UseFormSetValue<any>;
  isAnyNumber?: boolean;
}

export const InputCarNumber = ({
  id,
  className,
  name,
  value,
  register,
  placeholder = MaskCarNumber.Russia,
  isValid,
  isRequired,
  label,
  maxLength,
  error,
  helperMessage,
  dataTestId,
  isAnyNumber,
  setValue,
}: InputCarNumberWrapperProps) => {
  const [focus, setFocus] = useState<boolean>(false);
  const handleFocusAndBlur = useCallback(() => {
    setTimeout(() => {
      setFocus(prevState => !prevState);
    }, 200);
  }, []);

  const handleClearField = useCallback(() => {
    setValue(name, '', { shouldValidate: true });
  }, []);

  useEffect(() => {
    if (value.length) {
      setValue(name, '', { shouldValidate: true });
    }
  }, [isAnyNumber]);

  const handlerValue = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    event.target.value = event.target.value.replace(/[^a-zA-Z0-9а-яА-Я]/g, '');
    event.target.value = event.target.value.toUpperCase();
    setValue(event.target.name, event.target.value);
  };
  return (
    <div className={styles.Wrapper}>
      {label && (
        <label>
          {label}
          {helperMessage && <div className={styles.helperMessage}>{helperMessage}</div>}
        </label>
      )}
      <WrapperField isFocus={focus} error={error}>
        {isAnyNumber ? (
          <InputCarNumber.IsAnyNumber
            id={id}
            className={className}
            name={name}
            dataTestId={dataTestId}
            maxLength={maxLength}
            register={register}
            handlerValue={handlerValue}
            handleFocusAndBlur={handleFocusAndBlur}
          />
        ) : (
          <InputCarNumber.IsMask
            value={value}
            id={id}
            className={className}
            name={name}
            placeholder={placeholder}
            dataTestId={dataTestId}
            maxLength={maxLength}
            register={register}
            handlerValue={handlerValue}
            handleFocusAndBlur={handleFocusAndBlur}
          />
        )}
        <Icons isFocus={focus} value={value} isValid={isValid} isRequired={isRequired} handleClearField={handleClearField} />
      </WrapperField>
      <Error error={error} />
    </div>
  );
};

InputCarNumber.IsAnyNumber = function IsAnyNumber({ id, className, name, maxLength, dataTestId, register, handlerValue, handleFocusAndBlur }) {
  return (
    <Input
      id={id}
      className={className}
      name={name}
      type={'text'}
      {...register(name, { onChange: handlerValue })}
      placeholder={'Укажите Гос. номер'}
      maxLength={maxLength}
      onFocus={handleFocusAndBlur}
      onBlur={handleFocusAndBlur}
      data-testid={dataTestId}
    />
  );
};

InputCarNumber.IsMask = function IsMask({
  value,
  id,
  className,
  name,
  placeholder,
  maxLength,
  dataTestId,
  register,
  handlerValue,
  handleFocusAndBlur,
}) {
  return (
    <InputMask
      maskChar={null}
      mask="a999aa999"
      value={value}
      id={id}
      alwaysShowMask={true}
      className={className}
      name={name}
      type={'text'}
      dataTestId={dataTestId}
      {...register(name, { onChange: handlerValue })}
      placeholder={placeholder}
      onFocus={handleFocusAndBlur}
      onBlur={handleFocusAndBlur}
    >
      {inputProps => <Input maxLength={maxLength} {...inputProps} />}
    </InputMask>
  );
};
